import * as adminUserApi from '@apis/admin-user';
import * as productOrdersApi from '@apis/product-orders';
import { AdminUser } from '@models/auth';
import { AdminCreditPointLog } from '@models/credit-points';
import { JoinPromotionLog } from '@models/join-promotion';
import { PaymentStatementPurchase } from '@models/paymentStatement';
import { ProductOrderViewModel } from '@models/productOrderView';
import { SalesChannelsType } from '@models/sales-channels';
import { useQuery } from '@tanstack/react-query';
import { localTimeToUtcISOString, utcTimeToLocalISOString } from '@utils/date';
import { Button, Card, DatePicker, DatePickerProps, Spin, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import moment from 'moment';
import React, { useMemo, useState } from 'react';

interface UserPaymentTabProps {
  userId?: string;
  user?: AdminUser;
}

const STATUS_LABELS: Record<string, string> = {
  done: '결제완료',
  ready: '대기',
  cancelled: '취소',
  failed: '실패',
};

const formatDate = (date?: Date | string | null) => (date ? moment(date).format('YYYY-MM-DD HH:mm') : '-');
const formatNumber = (n?: number | null) => (typeof n === 'number' ? n.toLocaleString() : '-');

const TableTotalCount: React.FC<{ total?: number }> = ({ total }) => (
  <div style={{ marginBottom: 8, fontSize: 12, color: '#666' }}>총 {formatNumber(total ?? 0)}건</div>
);

/** KPI·요약용: 앱 / 카카오 / 네이버 / 베네피아 / 기타 */
function getChannelBucket(channel?: string): string {
  if (!channel || channel === SalesChannelsType.INBOUND_APP) return '앱';
  if (channel === SalesChannelsType.KAKAO_RESERVATION) return '카카오';
  if (channel === SalesChannelsType.NAVER_STORE || channel === SalesChannelsType.INBOUND_NAVER) return '네이버';
  if (channel === SalesChannelsType.BENEPIA) return '베네피아';
  return '기타';
}

/** 테이블·상세용 라벨 */
function getOrderChannelLabel(channel?: string): string {
  if (!channel || channel === SalesChannelsType.INBOUND_APP) return '앱';
  if (channel === SalesChannelsType.KAKAO_RESERVATION) return '카카오';
  if (channel === SalesChannelsType.NAVER_STORE) return '네이버 스토어';
  if (channel === SalesChannelsType.INBOUND_NAVER) return '네이버(인바운드)';
  if (channel === SalesChannelsType.BENEPIA) return '베네피아';
  return channel;
}

function getPurchasesList(record: ProductOrderViewModel): PaymentStatementPurchase[] {
  const purchase = (record as any).purchase as PaymentStatementPurchase | undefined;
  const purchases = (record as any).purchases as PaymentStatementPurchase[] | undefined;
  if (purchases?.length) return purchases;
  if (purchase) return [purchase];
  return [];
}

function formatPurchaseOptions(record: ProductOrderViewModel): string {
  const list = getPurchasesList(record);
  if (!list.length) return '-';
  return list
    .map((purchase: PaymentStatementPurchase) => {
      let optionName = '';
      if (purchase.option && purchase.option.length > 0) {
        optionName = `${purchase.option.join(' ')}`;
      }
      if (Array.isArray(purchase.name)) {
        return `${purchase.name.join('/')}(${purchase.count})`;
      }
      optionName = (purchase.name as string) || optionName;
      const newOptionNames: string[] = [];
      optionName.split(' / ').forEach((name) => {
        const newName = name.trim();
        if (newName && newName !== 'undefined' && !newOptionNames.includes(newName)) {
          newOptionNames.push(newName);
        }
      });
      optionName = newOptionNames.join(' / ') || purchase.originName || '';
      return `${optionName}(${purchase.count})`;
    })
    .join('\n');
}

function getSellerName(record: ProductOrderViewModel): string {
  const seller = (record as any).seller as
    | { name?: string; nickname?: string; profile?: { name?: string; nickname?: string } }
    | undefined;
  if (!seller) return '-';
  const name = seller.name || seller.profile?.name;
  const nickname = seller.nickname || seller.profile?.nickname;
  if (name && nickname) return `${name} (${nickname})`;
  return name || nickname || '-';
}

function stripPhoneHyphens(phone?: string | null): string | undefined {
  if (!phone) return undefined;
  const stripped = phone.replace(/-/g, '');
  return stripped || undefined;
}

function buildOrderSearchQuery(
  userId: string,
  buyerPhone: string | undefined,
  page: number,
  size: number,
  orderStartDate?: string | null,
  orderEndDate?: string | null,
) {
  const query: Record<string, unknown> = {
    buyerId: userId,
    page,
    size,
    dateType: 'createdAt',
  };

  if (buyerPhone) {
    query.buyerPhone = buyerPhone;
  }

  if (orderStartDate && orderEndDate) {
    query.startDate = orderStartDate;
    const endLocal = utcTimeToLocalISOString(orderEndDate);
    query.endDate = localTimeToUtcISOString(moment(endLocal).add(1, 'day').toISOString(), { timeToZero: true });
  }

  return query;
}

async function fetchAllOrdersForBuyer(
  userId: string,
  buyerPhone: string | undefined,
  orderStartDate?: string | null,
  orderEndDate?: string | null,
): Promise<ProductOrderViewModel[]> {
  const first = await productOrdersApi.searchProductOrders(
    buildOrderSearchQuery(userId, buyerPhone, 0, 1, orderStartDate, orderEndDate) as any,
  );
  const total = first.data?.total ?? 0;
  if (total === 0) return [];

  const pageSize = 500;
  const all: ProductOrderViewModel[] = [];
  let page = 0;
  const maxPages = 40;

  while (all.length < total && page < maxPages) {
    const res = await productOrdersApi.searchProductOrders(
      buildOrderSearchQuery(userId, buyerPhone, page, pageSize, orderStartDate, orderEndDate) as any,
    );
    const contents = (res.data?.contents || []) as ProductOrderViewModel[];
    all.push(...contents);
    if (contents.length < pageSize) break;
    page += 1;
  }
  return all;
}

const CHANNEL_BUCKET_ORDER = ['앱', '카카오', '네이버', '베네피아', '기타'] as const;

function aggregateOrders(orders: ProductOrderViewModel[]) {
  const byBucket: Record<string, number> = {};
  const byBucketAmount: Record<string, number> = {};
  const byBucketRefund: Record<string, number> = {};
  let totalAmount = 0;
  let refundCount = 0;
  for (const o of orders) {
    const bucket = getChannelBucket(o.channel);
    const amount = o.payment?.amount || 0;
    byBucket[bucket] = (byBucket[bucket] || 0) + 1;
    byBucketAmount[bucket] = (byBucketAmount[bucket] || 0) + amount;
    totalAmount += amount;
    if (o.statuses?.refund === 'all' || o.statuses?.refund === 'partial') {
      refundCount += 1;
      byBucketRefund[bucket] = (byBucketRefund[bucket] || 0) + 1;
    }
  }
  return { byBucket, byBucketAmount, byBucketRefund, totalAmount, refundCount, totalCount: orders.length };
}

function renderChannelBreakdownSub(loading: boolean, lines: string[]): React.ReactNode {
  if (loading) return <Spin size="small" />;
  if (!lines.length) return <span>내역 없음</span>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {lines.map((line) => (
        <div key={line}>{line}</div>
      ))}
    </div>
  );
}

const KpiCard: React.FC<{ label: string; value: React.ReactNode; subText?: React.ReactNode }> = ({
  label,
  value,
  subText,
}) => (
  <div
    style={{
      flex: 1,
      minWidth: 140,
      padding: 16,
      border: '1px solid #eaeaea',
      borderRadius: 8,
      backgroundColor: '#fafafa',
    }}>
    <div style={{ color: '#666', fontSize: 12, marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 20, fontWeight: 'bold' }}>{value}</div>
    {subText && <div style={{ color: '#0180ef', fontSize: 11, marginTop: 8 }}>{subText}</div>}
  </div>
);

const JOIN_PROMOTION_STATUS_LABELS: Record<string, string> = {
  success: '지급',
  rejected: '지급 거절',
};

export const UserPaymentTab: React.FC<UserPaymentTabProps> = ({ userId, user }) => {
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [joinPromotionPage, setJoinPromotionPage] = useState(0);
  const [joinPromotionSize, setJoinPromotionSize] = useState(10);
  const [orderStartDate, setOrderStartDate] = useState<string | null>(null);
  const [orderEndDate, setOrderEndDate] = useState<string | null>(null);
  const rewardCode = (user as AdminUser & { rewardCode?: string })?.rewardCode;
  const buyerPhone = stripPhoneHyphens(user?.profile?.phone);

  const ordersQuery = useQuery({
    queryKey: [`user-detail-orders-${userId}`, buyerPhone, page, size, orderStartDate, orderEndDate],
    queryFn: () =>
      productOrdersApi.searchProductOrders(
        buildOrderSearchQuery(userId!, buyerPhone, page, size, orderStartDate, orderEndDate) as any,
      ),
    enabled: !!userId,
  });

  const onChangeOrderStartDate: DatePickerProps['onChange'] = (date) => {
    setPage(0);
    setOrderStartDate(localTimeToUtcISOString(date ? date.toDate() : null, { timeToZero: true }));
  };

  const onChangeOrderEndDate: DatePickerProps['onChange'] = (date) => {
    setPage(0);
    setOrderEndDate(localTimeToUtcISOString(date ? date.toDate() : null, { timeToZero: true }));
  };

  const clearOrderDateFilter = () => {
    setPage(0);
    setOrderStartDate(null);
    setOrderEndDate(null);
  };

  const ordersSummaryQuery = useQuery({
    queryKey: [`user-detail-orders-summary-${userId}`, buyerPhone, orderStartDate, orderEndDate],
    queryFn: () => fetchAllOrdersForBuyer(userId!, buyerPhone, orderStartDate, orderEndDate),
    enabled: !!userId,
  });

  const hasOrderDateFilter = !!(orderStartDate && orderEndDate);
  const kpiPeriodLabel = hasOrderDateFilter ? '(선택 기간)' : '(전체)';

  const pointLogsQuery = useQuery({
    queryKey: [`user-detail-point-logs-${userId}`],
    queryFn: () => adminUserApi.getUserCreditPointLogs(userId!),
    enabled: !!userId,
  });

  const joinPromotionLogsQuery = useQuery({
    queryKey: [`user-detail-join-promotion-logs-${userId}`, joinPromotionPage, joinPromotionSize],
    queryFn: () =>
      adminUserApi.getUserJoinPromotionLogs(userId!, {
        page: joinPromotionPage,
        size: joinPromotionSize,
      }),
    enabled: !!userId,
  });

  const orderResult = ordersQuery.data?.data;
  const orders: ProductOrderViewModel[] = orderResult?.contents || [];
  const totalOrders = orderResult?.total ?? 0;

  const summary = useMemo(() => {
    const all = ordersSummaryQuery.data || [];
    return aggregateOrders(all);
  }, [ordersSummaryQuery.data]);

  const channelBreakdownLines = useMemo(() => {
    return CHANNEL_BUCKET_ORDER.filter((k) => (summary.byBucket[k] || 0) > 0).map(
      (k) => `${k}: ${summary.byBucket[k]}건`,
    );
  }, [summary.byBucket]);

  const channelAmountBreakdownLines = useMemo(() => {
    const total = summary.totalAmount;
    return CHANNEL_BUCKET_ORDER.filter((k) => (summary.byBucketAmount[k] || 0) > 0).map((k) => {
      const amount = summary.byBucketAmount[k] || 0;
      const pct = total > 0 ? Math.round((amount / total) * 100) : 0;
      return `${k}: ${formatNumber(amount)}원 (${pct}%)`;
    });
  }, [summary.byBucketAmount, summary.totalAmount]);

  const channelRefundBreakdownLines = useMemo(() => {
    const total = summary.refundCount;
    return CHANNEL_BUCKET_ORDER.filter((k) => (summary.byBucketRefund[k] || 0) > 0).map((k) => {
      const count = summary.byBucketRefund[k] || 0;
      const pct = total > 0 ? Math.round((count / total) * 100) : 0;
      return `${k}: ${formatNumber(count)}건 (${pct}%)`;
    });
  }, [summary.byBucketRefund, summary.refundCount]);

  const summaryLoading = ordersSummaryQuery.isLoading;
  const countBreakdownSub = useMemo(
    () => renderChannelBreakdownSub(summaryLoading, channelBreakdownLines),
    [summaryLoading, channelBreakdownLines],
  );
  const amountBreakdownSub = useMemo(
    () => renderChannelBreakdownSub(summaryLoading, channelAmountBreakdownLines),
    [summaryLoading, channelAmountBreakdownLines],
  );
  const refundBreakdownSub = useMemo(
    () => renderChannelBreakdownSub(summaryLoading, channelRefundBreakdownLines),
    [summaryLoading, channelRefundBreakdownLines],
  );

  const pointLogs: AdminCreditPointLog[] = pointLogsQuery.data?.data || [];
  const joinPromotionResult = joinPromotionLogsQuery.data?.data;
  const joinPromotionLogs: JoinPromotionLog[] = joinPromotionResult?.contents || [];
  const totalJoinPromotionLogs = joinPromotionResult?.total ?? 0;

  const orderColumns: ColumnsType<ProductOrderViewModel> = [
    {
      title: '결제일',
      dataIndex: 'createdAt',
      width: 130,
      render: (createdAt) => formatDate(createdAt),
    },
    {
      title: '채널',
      dataIndex: 'channel',
      width: 100,
      render: (channel: string | undefined) => <span style={{ fontSize: 12 }}>{getOrderChannelLabel(channel)}</span>,
    },
    {
      title: '상품',
      dataIndex: 'product',
      render: (product) => (
        <div style={{ whiteSpace: 'pre-wrap' }}>
          <div style={{ fontWeight: 500 }}>{product?.name || '-'}</div>
          {product?.saleType && <div style={{ color: '#888', fontSize: 11 }}>{product.saleType}</div>}
        </div>
      ),
    },
    {
      title: '판매자',
      width: 140,
      render: (_, record) => <div style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>{getSellerName(record)}</div>,
    },
    {
      title: '구매 옵션',
      width: 220,
      render: (_, record) => (
        <div style={{ fontSize: 11, whiteSpace: 'pre-wrap', textAlign: 'left' }}>{formatPurchaseOptions(record)}</div>
      ),
    },
    {
      title: '결제 금액',
      dataIndex: 'payment',
      width: 150,
      align: 'right',
      render: (payment: ProductOrderViewModel['payment']) => {
        const point = payment?.point;
        const coupon = payment?.couponDiscountAmount;
        const membership = payment?.membershipDiscountAmount;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-end' }}>
            <div style={{ fontWeight: 600 }}>{formatNumber(payment?.amount)}원</div>
            {typeof point === 'number' && point > 0 && (
              <div style={{ fontSize: 11, color: '#0180ef' }}>포인트 {formatNumber(point)}원</div>
            )}
            {typeof coupon === 'number' && coupon > 0 && (
              <div style={{ fontSize: 11, color: '#d944db' }}>쿠폰 할인 {formatNumber(coupon)}원</div>
            )}
            {typeof membership === 'number' && membership > 0 && (
              <div style={{ fontSize: 11, color: '#888' }}>멤버십 할인 {formatNumber(membership)}원</div>
            )}
          </div>
        );
      },
    },
    {
      title: '상태',
      dataIndex: 'statuses',
      width: 140,
      render: (statuses) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11 }}>
          {statuses?.payment && <div>결제: {STATUS_LABELS[statuses.payment] || statuses.payment}</div>}
          {statuses?.refund && <div style={{ color: 'red' }}>환불: {statuses.refund}</div>}
          {statuses?.settlement && <div>정산: {statuses.settlement}</div>}
        </div>
      ),
    },
    {
      title: '주문번호',
      width: 120,
      render: (record) => <span style={{ fontSize: 11, color: '#888' }}>{record.uuid || record.uid || '-'}</span>,
    },
  ];

  const joinPromotionColumns: ColumnsType<JoinPromotionLog> = [
    {
      title: '가입 유저',
      width: 220,
      render: (_, record) => {
        const name = record.user?.profile?.name || record.user?.profile?.nickname;
        const phone = record.user?.profile?.phone || record.userPhone;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12 }}>
            {name ? <div style={{ fontWeight: 500 }}>{name}</div> : null}
            {phone ? <div>{phone}</div> : null}
            <div style={{ fontSize: 11, color: '#888' }}>{record.userId || '-'}</div>
          </div>
        );
      },
    },
    {
      title: '지급 포인트',
      width: 100,
      align: 'right',
      render: (_, record) => formatNumber(record.creditPoint),
    },
    {
      title: '상태',
      width: 160,
      render: (_, record) => (
        <div style={{ fontSize: 12 }}>
          <div>{record.status ? JOIN_PROMOTION_STATUS_LABELS[record.status] || record.status : '-'}</div>
          {record.reason && <div style={{ color: '#888', fontSize: 11 }}>{record.reason}</div>}
        </div>
      ),
    },
    {
      title: '생성',
      dataIndex: 'createdAt',
      width: 150,
      render: (createdAt) => formatDate(createdAt),
    },
  ];

  const pointColumns: ColumnsType<AdminCreditPointLog> = [
    {
      title: '구분',
      dataIndex: 'flag',
      width: 70,
      align: 'center',
      render: (flag) => (
        <span
          style={{
            color: 'white',
            borderRadius: 12,
            padding: '2px 8px',
            backgroundColor: flag === '+' ? '#0180ef' : '#d944db',
            fontSize: 11,
          }}>
          {flag === '+' ? '적립' : '차감'}
        </span>
      ),
    },
    {
      title: '내용',
      dataIndex: 'content',
    },
    {
      title: '금액',
      dataIndex: 'amount',
      width: 110,
      align: 'right',
      render: (amount) => formatNumber(amount),
    },
    {
      title: '일시',
      dataIndex: 'createdAt',
      width: 150,
      render: (date) => formatDate(date),
    },
  ];

  const orderDateFilter = (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 12, color: '#666' }}>기간</span>
      <DatePicker
        allowClear
        placeholder="시작일"
        value={orderStartDate ? dayjs(utcTimeToLocalISOString(orderStartDate)!) : null}
        onChange={onChangeOrderStartDate}
      />
      <span style={{ color: '#888' }}>~</span>
      <DatePicker
        allowClear
        placeholder="종료일"
        value={orderEndDate ? dayjs(utcTimeToLocalISOString(orderEndDate)!) : null}
        onChange={onChangeOrderEndDate}
      />
      {(orderStartDate || orderEndDate) && (
        <Button size="small" onClick={clearOrderDateFilter}>
          초기화
        </Button>
      )}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {orderDateFilter}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <KpiCard
          label={`총 결제 건수 ${kpiPeriodLabel}`}
          value={summaryLoading ? <Spin size="small" /> : formatNumber(summary.totalCount)}
          subText={countBreakdownSub}
        />
        <KpiCard
          label={`총 결제 금액 ${kpiPeriodLabel}`}
          value={summaryLoading ? <Spin size="small" /> : `${formatNumber(summary.totalAmount)}원`}
          subText={amountBreakdownSub}
        />
        <KpiCard
          label={`환불 건수 ${kpiPeriodLabel}`}
          value={summaryLoading ? <Spin size="small" /> : formatNumber(summary.refundCount)}
          subText={refundBreakdownSub}
        />
        <KpiCard label="보유 포인트" value={formatNumber(user?.profile?.creditPoints)} />
      </div>

      <Card title="결제 내역" size="small">
        {ordersQuery.isLoading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <Spin />
          </div>
        ) : (
          <Table<ProductOrderViewModel>
            rowKey={(record) => `${record._id}`}
            columns={orderColumns}
            dataSource={orders}
            size="small"
            scroll={{ x: 'max-content' }}
            pagination={{
              current: page + 1,
              pageSize: size,
              total: totalOrders,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50'],
              onChange: (nextPage, nextSize) => {
                setPage((nextPage || 1) - 1);
                setSize(nextSize || 10);
              },
            }}
          />
        )}
      </Card>

      <Card title="포인트 변동 내역" size="small">
        {pointLogsQuery.isLoading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <Spin />
          </div>
        ) : (
          <Table<AdminCreditPointLog>
            rowKey={(record) => `${record._id}`}
            columns={pointColumns}
            dataSource={pointLogs}
            size="small"
            pagination={{ pageSize: 10 }}
          />
        )}
      </Card>

      <Card
        title="초대 가입 내역"
        size="small"
        extra={
          rewardCode ? (
            <span style={{ fontSize: 12, color: '#666' }}>리워드 코드: {rewardCode}</span>
          ) : (
            <span style={{ fontSize: 12, color: '#888' }}>리워드 코드 없음</span>
          )
        }>
        {joinPromotionLogsQuery.isLoading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <Spin />
          </div>
        ) : (
          <>
            <TableTotalCount total={totalJoinPromotionLogs} />
            <Table<JoinPromotionLog>
              rowKey={(record) => `${record._id}`}
              columns={joinPromotionColumns}
              dataSource={joinPromotionLogs}
              size="small"
              scroll={{ x: 'max-content' }}
              locale={{ emptyText: '초대 코드로 가입한 내역이 없습니다.' }}
              pagination={{
                current: joinPromotionPage + 1,
                pageSize: joinPromotionSize,
                total: totalJoinPromotionLogs,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50'],
                onChange: (nextPage, nextSize) => {
                  setJoinPromotionPage((nextPage || 1) - 1);
                  setJoinPromotionSize(nextSize || 10);
                },
              }}
            />
          </>
        )}
      </Card>
    </div>
  );
};
